import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, TextField, Typography, useTheme } from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { Purchase } from 'src/dto/Credits';
import { PurchaseCreatePayload } from 'src/redux/services/purchases';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import ButtonComponent from 'src/components/ButtonComponent';

interface PurchaseCreateModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (a: Purchase) => void;
  onCreate: (values: PurchaseCreatePayload) => void;
  initData: Purchase | null;
  appKey: string;
}

export const PurchaseCreateModal: React.FC<PurchaseCreateModalProps> = ({ open, onClose, initData, onUpdate, onCreate, appKey }) => {
  const theme = useTheme();

  const activation_date = new Date(initData?.activation_date || '');
  const expiration_date = new Date(initData?.expiration_date || '');

  return (
    <Formik
      initialValues={{
        app_key: initData?.app_key ?? '',
        value: initData?.value.toString().replace('.', ',') ?? 0,
        activation_date,
        expiration_date,
      }}
      enableReinitialize
      validateOnChange={true}
      validate={(values) => {
        let errors: FormikErrors<Purchase> = {};
        /* eslint-disable prefer-regex-literals */
        const regExp = new RegExp('^[0-9]*[,0-9]*$');
        if (values.value === '') {
          errors.value = 'Should not be empty';
        }
        if (!regExp.test(values.value.toString())) {
          errors.value = 'Should be an integer number or a number with comma separator';
        }
        if (!values.activation_date || new Date(values.activation_date).getTime().toString() === 'NaN') {
          errors.activation_date = 'Field is mandatory';
        }
        if (!values.expiration_date || new Date(values.expiration_date).getTime().toString() === 'NaN') {
          errors.expiration_date = 'Field is mandatory';
        }
        if (new Date(values.activation_date).getTime() > new Date(values.expiration_date).getTime()) {
          errors = {
            ...errors,
            activation_date: 'Creation date should be less than expiration date',
            expiration_date: 'Creation date should be less than expiration date',
          };
        }

        return errors;
      }}
      onSubmit={(values, { resetForm }) => {
        const newValue = parseFloat(values.value.toString().replace(',', '.'));
        if (initData) {
          onUpdate({
            ...initData,
            ...values,
            value: newValue,
          });
        } else {
          onCreate({
            ...values,
            app_key: appKey,
            value: newValue,
            frequency: 'pay_to_go',
            source: 'INITIAL_CREDIT',
          });
        }
        resetForm();
      }}
    >
      {(formikProps) => {
        const {
          errors: { value: valueError, activation_date: activationDateError, expiration_date: expirationDateError },
        } = formikProps;
        return (
          <Dialog fullWidth={true} maxWidth="sm" open={open} onClose={onClose}>
            <DialogTitle>
              <Grid container justifyContent="space-between" alignItems="center">
                <Typography variant="h4">{initData ? 'Edit purchase' : 'Purchase'}</Typography>
                <ButtonComponent buttontype="icon" icon={<CloseIcon />} onClick={onClose} />
              </Grid>
            </DialogTitle>

            <Divider light />

            <DialogContent style={{ overflow: 'hidden', paddingTop: 25 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formikProps.handleSubmit();
                }}
              >
                <Grid container={true} spacing={3}>
                  <Grid item={true} xs={12}>
                    <TextField
                      variant="outlined"
                      label="Amount"
                      error={!!valueError}
                      fullWidth={true}
                      onChange={(result) => {
                        formikProps.setFieldValue('value', result.target.value);
                      }}
                      value={formikProps.getFieldProps('value').value}
                      helperText={formikProps.errors.value}
                    />
                  </Grid>

                  <Grid item={true} xs={12}>
                    <DesktopDatePicker
                      label="Start date"
                      inputFormat="dd/MM/yyyy"
                      onChange={(result) => {
                        formikProps.setFieldValue('activation_date', result);
                      }}
                      value={formikProps.getFieldProps('activation_date').value}
                      PaperProps={{
                        sx: {
                          '& .MuiPickersDay-root': {
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          {...formikProps.getFieldProps('activation_date')}
                          error={!!activationDateError && formikProps.getFieldMeta('activation_date').touched}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item={true} xs={12}>
                    <DesktopDatePicker
                      label="Expiration date"
                      inputFormat="dd/MM/yyyy"
                      onChange={(result) => {
                        formikProps.setFieldValue('expiration_date', result);
                      }}
                      value={formikProps.getFieldProps('expiration_date').value}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          {...formikProps.getFieldProps('expiration_date')}
                          error={!!expirationDateError && formikProps.getFieldMeta('expiration_date').touched}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </form>
            </DialogContent>

            <Divider light />

            <DialogActions>
              <ButtonComponent buttontype="cancel" onClick={onClose} sx={{ marginRight: theme.spacing(3) }} />

              <ButtonComponent
                buttontype="mainaction"
                text="Save"
                disabled={!(formikProps.isValid && formikProps.dirty)}
                onClick={() => formikProps.submitForm()}
              />
            </DialogActions>
          </Dialog>
        );
      }}
    </Formik>
  );
};
